/**
 * Client API admin — login, verify, gestion des settings.
 *
 * Stocke le token dans localStorage (`mizan_admin_token`). Auto-purge sur
 * 401/expiration. À utiliser uniquement depuis les pages /admin/*.
 */

const TOKEN_KEY = 'mizan_admin_token';
const EXPIRES_KEY = 'mizan_admin_token_exp';

const DEFAULT_API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export interface AdminSetting {
  key: string;
  description: string;
  is_secret: boolean;
  configured: boolean;
  source: 'd1' | 'env' | 'none';
  masked_value: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface AdminApiError extends Error {
  status: number;
  body: unknown;
}

function makeError(status: number, message: string, body: unknown): AdminApiError {
  const err = new Error(message) as AdminApiError;
  err.status = status;
  err.body = body;
  return err;
}

async function adminRequest<T>(
  path: string,
  init?: RequestInit & { auth?: boolean },
): Promise<T> {
  const useAuth = init?.auth !== false;
  const token = useAuth ? localStorage.getItem(TOKEN_KEY) : null;
  const res = await fetch(`${DEFAULT_API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  const text = await res.text();
  let body: unknown = text;
  try {
    body = JSON.parse(text);
  } catch {
    /* keep as text */
  }
  if (!res.ok) {
    if (res.status === 401 && useAuth) clearAdminToken();
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`;
    throw makeError(res.status, message, body);
  }
  return body as T;
}

export const adminApi = {
  login: (password: string) =>
    adminRequest<{ token: string; expires_at: string; sub: string }>(
      '/admin/auth/login',
      { method: 'POST', body: JSON.stringify({ password }), auth: false },
    ),

  verify: () => adminRequest<{ valid: boolean; sub: string; exp: number }>('/admin/auth/verify'),

  listSettings: () => adminRequest<{ settings: AdminSetting[] }>('/admin/settings'),

  updateSetting: (key: string, value: string) =>
    adminRequest<{ ok: boolean; key: string }>(`/admin/settings/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    }),

  deleteSetting: (key: string) =>
    adminRequest<{ ok: boolean; key: string }>(`/admin/settings/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    }),

  // ----- Firms -----
  listFirms: () => adminRequest<{ firms: AdminFirm[] }>('/admin/firms'),
  createFirm: (input: {
    name: string;
    legal_name: string;
    matricule_fiscal: string;
    plan?: 'starter' | 'essentiel' | 'pro' | 'enterprise';
  }) =>
    adminRequest<{ ok: boolean; firm: AdminFirm }>('/admin/firms', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // ----- Users -----
  listUsers: () => adminRequest<{ users: AdminUser[] }>('/admin/users'),
  createUser: (input: {
    email: string;
    password: string;
    full_name: string;
    firm_id: string;
    role: 'owner' | 'accountant' | 'assistant' | 'viewer';
  }) =>
    adminRequest<{ ok: boolean; user: AdminUser }>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  resetPassword: (userId: string, password: string) =>
    adminRequest<{ ok: boolean }>(`/admin/users/${encodeURIComponent(userId)}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    }),
};

export interface AdminFirm {
  id: string;
  name: string;
  legal_name: string;
  matricule_fiscal: string;
  plan: 'starter' | 'essentiel' | 'pro' | 'enterprise';
  created_at?: string;
}

export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  firm_id: string | null;
  firm_name: string | null;
  role: 'owner' | 'accountant' | 'assistant' | 'viewer' | null;
  created_at: string;
}

export function saveAdminToken(token: string, expiresAt: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(EXPIRES_KEY, expiresAt);
}

export function getAdminToken(): { token: string; expires_at: string } | null {
  const token = localStorage.getItem(TOKEN_KEY);
  const exp = localStorage.getItem(EXPIRES_KEY);
  if (!token || !exp) return null;
  if (new Date(exp).getTime() < Date.now()) {
    clearAdminToken();
    return null;
  }
  return { token, expires_at: exp };
}

export function clearAdminToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRES_KEY);
}
